import { convexQuery } from "@convex-dev/react-query";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";

import { categoriesGetAll, itemsGetAll } from "~/api-refs";

import { LoadingPage } from "../../shared/loading-page";
import { Button } from "../../ui/button";
import { DialogDescription, DialogHeader, DialogTitle } from "../../ui/dialog";
import { ApplyToCategoriesOrItemsForm } from "./apply-to-categories-or-items-form";

interface ApplyNewModifierFormProps {
  onSubmit: (data: { categories: string[]; items: string[] }) => void;
  isPending: boolean;
}

export function ApplyNewModifierForm({
  onSubmit,
  isPending,
}: ApplyNewModifierFormProps) {
  const { data: categoriesResult } = useQuery(
    convexQuery(categoriesGetAll, {
      includeMenus: true,
      includeItemsCount: true,
      paginationOpts: { numItems: 100, cursor: null },
    })
  );
  const categories = categoriesResult?.page;
  const { data: itemsResult } = useQuery(
    convexQuery(itemsGetAll, {
      includeCategoryRelations: true,
      includeMenus: true,
      includeModifierGroupsCount: true,
      paginationOpts: { numItems: 100, cursor: null },
    })
  );
  const items = itemsResult?.page;

  const [values, setValues] = useState<{
    categories: string[];
    items: string[];
  }>({
    categories: [],
    items: [],
  });

  const [error, setError] = useState<string | null>(null);

  if (!(categories && items)) {
    return <LoadingPage />;
  }

  return (
    <form
      className="space-y-5"
      onSubmit={(e) => {
        e.preventDefault();
        if (!(values.categories.length || values.items.length)) {
          setError("Please select at least one category or item");
          return;
        }
        setError(null);
        onSubmit(values);
      }}
    >
      <DialogHeader className="items-center space-y-0">
        <DialogTitle>Apply new modifiers</DialogTitle>
        <DialogDescription>
          Add customisation to categories or items
        </DialogDescription>
      </DialogHeader>
      <ApplyToCategoriesOrItemsForm
        categories={categories}
        items={items}
        setError={setError}
        setValues={setValues}
        values={values}
      />
      {error && <p className="body-sm text-destructive">{error}</p>}
      <Button className="w-full" disabled={isPending} size="lg" type="submit">
        Create Modifier
      </Button>
    </form>
  );
}
